import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { useTheme } from '../context/ThemeContext';

const CITY_LIST = ['Houston', 'Atlanta', 'New York', 'Toronto', 'Washington DC', 'Miami', 'Dallas'];
const INTEREST_OPTIONS = [
  'Afrobeats',
  'Food & Culture',
  'Art & Fashion',
  'Comedy',
  'Faith & Gospel',
  'Sports',
  'Business',
  'African Heritage',
];

export default function OnboardingPage() {
  const { VB } = useTheme();
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [city, setCity] = useState('');
  const [interests, setInterests] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState({ city: '', interests: [] });

  const toggleInterest = (interest) => {
    setInterests((prev) => prev.includes(interest) ? prev.filter((item) => item !== interest) : [...prev, interest]);
  };

  const finish = async (withPreferences) => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = { hasCompletedOnboarding: true };
      if (withPreferences) {
        payload.city = city;
        payload.interests = interests;
      }
      await api.patch('/users/me', payload);
      await refreshUser();
      // Show brief confirmation screen before navigating
      if (withPreferences && (city || interests.length > 0)) {
        setConfirmationData({ city, interests });
        setShowConfirmation(true);
        setSaving(false);
        setTimeout(() => {
          navigate('/');
        }, 1500);
        return;
      }
    } catch (err) {
      console.error('Failed to save onboarding preferences', err);
    } finally {
      setSaving(false);
      if (!withPreferences || !(city || interests.length > 0)) {
        navigate('/');
      }
    }
  };

  if (showConfirmation) {
    const cityLabel = confirmationData.city || 'your city';
    const interestsLabel = confirmationData.interests.length > 0
      ? confirmationData.interests.slice(0, 3).join(', ') + (confirmationData.interests.length > 3 ? ' & more' : '')
      : 'your interests';
    return (
      <div style={{ minHeight: '100vh', background: VB.void, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ width: '100%', maxWidth: 520, background: VB.darkCard, border: `1px solid ${VB.borderDark}`, borderRadius: 20, padding: 40, textAlign: 'center' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(16,185,129,0.15)', border: '2px solid #10B981', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', color: '#10B981', fontSize: 28, fontWeight: 700 }}>
            ✓
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: VB.snowPrimary, margin: '0 0 12px' }}>You're all set!</h2>
          <p style={{ fontSize: 14, color: VB.snowMuted, lineHeight: 1.7, margin: 0 }}>
            Showing you events in <strong style={{ color: VB.snowPrimary }}>{cityLabel}</strong>
            {confirmationData.interests.length > 0 && (
              <> for <strong style={{ color: VB.snowPrimary }}>{interestsLabel}</strong></>
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: VB.void, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 520, background: VB.darkCard, border: `1px solid ${VB.borderDark}`, borderRadius: 20, padding: 28 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: VB.snowPrimary, margin: 0 }}>Almost there!</h1>
        <p style={{ fontSize: 14, color: VB.snowMuted, margin: '8px 0 18px' }}>Help us personalize Vibana for you</p>

        <div style={{ width: '100%', height: 1, background: VB.borderDark, marginBottom: 20 }} />

        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2, color: VB.red, marginBottom: 8 }}>Your city</div>
        <select
          value={city}
          onChange={(e) => setCity(e.target.value)}
          aria-label="Select your city"
          style={{ width: '100%', background: VB.void, color: VB.snowPrimary, border: `1px solid ${VB.borderDark}`, borderRadius: 12, padding: '12px 14px', marginBottom: 20 }}
        >
          <option value="">Select your city</option>
          {CITY_LIST.map((cityOption) => <option key={cityOption} value={cityOption}>{cityOption}</option>)}
        </select>

        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2, color: VB.red, marginBottom: 10 }}>Events you love</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 22 }}>
          {INTEREST_OPTIONS.map((interest) => {
            const selected = interests.includes(interest);
            return (
              <button
                key={interest}
                type="button"
                onClick={() => toggleInterest(interest)}
                style={{
                  borderRadius: 999,
                  padding: '9px 14px',
                  fontSize: 12,
                  cursor: 'pointer',
                  border: selected ? `1px solid ${VB.red}` : `1px solid ${VB.borderDark}`,
                  background: selected ? VB.red : 'transparent',
                  color: selected ? VB.white : VB.snowMuted,
                }}
              >
                {interest}
              </button>
            );
          })}
        </div>

        <button
          onClick={() => finish(true)}
          disabled={saving}
          style={{ width: '100%', border: 'none', borderRadius: 8, padding: '14px 18px', background: VB.red, color: VB.white, fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
        >
          {saving ? 'Saving...' : 'Done! Take me in →'}
        </button>

        <button
          type="button"
          onClick={() => finish(false)}
          disabled={saving}
          style={{ width: '100%', marginTop: 12, border: 'none', background: 'transparent', color: VB.snowMuted, fontSize: 12, cursor: 'pointer' }}
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}
